import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, fmtMoney, fmtDate } from "@/lib/api";
import { SpeakUpLogo } from "@/components/Layout";
import { Trophy, ArrowRight, Filter } from "lucide-react";

const publicApi = axios.create({ baseURL: API });

const ISSUE_LABELS = {
    "wrong/excess charge": "Wrong/excess charge",
    "failed transaction": "Failed transaction",
    "unauthorized debit": "Unauthorized debit",
    "loan/EMI issue": "Loan / EMI issue",
    "card issue": "Card issue",
    "mis-selling": "Mis-selling",
    "service deficiency": "Service deficiency",
    other: "Other",
};

export default function Wins() {
    const [items, setItems] = useState(null);
    const [stats, setStats] = useState(null);
    const [filter, setFilter] = useState("");

    useEffect(() => {
        const q = filter ? `?entity=${encodeURIComponent(filter)}` : "";
        publicApi.get(`/wins${q}`).then((r) => setItems(r.data.items));
        publicApi.get(`/wins/stats`).then((r) => setStats(r.data));
    }, [filter]);

    // Build entity-name facets for filter chips
    const facets = (items || []).reduce((acc, w) => {
        acc[w.entity_name] = (acc[w.entity_name] || 0) + 1;
        return acc;
    }, {});
    const facetNames = Object.keys(facets).sort();

    return (
        <div className="min-h-screen bg-white text-[var(--speakup-text)]">
            <header className="sticky top-0 z-30 bg-white/85 backdrop-blur border-b border-[var(--speakup-border)]">
                <div className="max-w-5xl mx-auto px-5 py-3 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2" data-testid="wins-home-link">
                        <SpeakUpLogo size={26} />
                        <span className="display text-[18px] tracking-tight" style={{ color: "var(--speakup-text-strong)" }}>
                            SpeakUp
                        </span>
                    </Link>
                    <Link
                        to="/auth"
                        data-testid="wins-cta-auth"
                        className="h-10 px-4 inline-flex items-center gap-1.5 rounded-[10px] text-white text-sm font-semibold"
                        style={{ background: "var(--speakup-red)" }}
                    >
                        Raise your complaint <ArrowRight size={14} />
                    </Link>
                </div>
            </header>

            <main className="max-w-5xl mx-auto px-5 py-8 speakup-fade-up">
                <div className="flex items-center gap-2 chip chip-resolved" data-testid="wins-page-pill">
                    <Trophy size={14} /> Wall of Wins
                </div>
                <h1 className="display text-[32px] md:text-[42px] mt-3 leading-[1.1] max-w-3xl">
                    Real outcomes against Indian banks, NBFCs and card issuers.
                </h1>
                <p className="text-[var(--speakup-text-muted)] mt-3 max-w-2xl text-[16px]">
                    Anonymised, opt-in entries from people who used SpeakUp to draft and escalate their grievance.
                    No names. No phone numbers. No account or card digits. Only the institution, the issue type, and
                    how long it took.
                </p>

                {/* Stats */}
                <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="wins-stats">
                    <Stat label="Wins published" value={stats?.total_wins ?? "—"} />
                    <Stat label="Total recovered (where amount shown)" value={stats ? fmtMoney(stats.total_amount_recovered) : "—"} />
                    <Stat label="Institutions on the wall" value={facetNames.length || "—"} />
                </div>

                {/* Filter chips */}
                {facetNames.length > 0 && (
                    <div className="mt-7" data-testid="wins-filter-block">
                        <div className="text-[11px] uppercase tracking-wider text-[var(--speakup-text-muted)] flex items-center gap-1.5 mb-2">
                            <Filter size={12} /> Filter by institution
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <button
                                onClick={() => setFilter("")}
                                data-testid="wins-filter-all"
                                className={`h-9 px-3 rounded-full text-[13px] font-semibold border ${
                                    !filter
                                        ? "text-white border-transparent"
                                        : "bg-white border-[var(--speakup-border)]"
                                }`}
                                style={!filter ? { background: "var(--speakup-text-strong)" } : {}}
                            >
                                All
                            </button>
                            {facetNames.map((name) => (
                                <button
                                    key={name}
                                    onClick={() => setFilter(name)}
                                    data-testid={`wins-filter-${name.replace(/\s+/g, "-").toLowerCase()}`}
                                    className={`h-9 px-3 rounded-full text-[13px] font-semibold border ${
                                        filter === name
                                            ? "text-white border-transparent"
                                            : "bg-white border-[var(--speakup-border)]"
                                    }`}
                                    style={filter === name ? { background: "var(--speakup-text-strong)" } : {}}
                                >
                                    {name} <span className="opacity-60 ml-1 tabular">{facets[name]}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Feed */}
                <div className="mt-7 grid md:grid-cols-2 gap-4" data-testid="wins-feed">
                    {items === null && (
                        <div className="speakup-card p-6 text-[var(--speakup-text-muted)]">Loading wins…</div>
                    )}
                    {items?.length === 0 && (
                        <div className="speakup-card p-7 md:col-span-2" data-testid="wins-empty">
                            <div className="display text-[20px]">No wins yet</div>
                            <p className="text-[var(--speakup-text-muted)] mt-2">
                                Be the first. Resolve your grievance with SpeakUp's help and opt in to add your win
                                — anonymously.
                            </p>
                            <Link
                                to="/auth"
                                className="mt-4 inline-flex items-center gap-2 h-11 px-4 rounded-[10px] text-white font-semibold"
                                style={{ background: "var(--speakup-red)" }}
                            >
                                Start your complaint <ArrowRight size={14} />
                            </Link>
                        </div>
                    )}
                    {items?.map((w) => (
                        <WinCard key={w.id} win={w} />
                    ))}
                </div>

                {/* CTA strip */}
                <div
                    className="mt-12 rounded-[14px] p-7 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-5"
                    style={{
                        background:
                            "linear-gradient(180deg, rgba(242,59,54,0.06) 0%, rgba(242,59,54,0.02) 100%)",
                        border: "1px solid rgba(242,59,54,0.18)",
                    }}
                    data-testid="wins-cta-strip"
                >
                    <div>
                        <div className="display text-[22px]">Got a similar issue?</div>
                        <p className="text-[var(--speakup-text-muted)] mt-1 max-w-xl">
                            Speak it in your language. We'll draft the formal complaint and walk it up the
                            escalation ladder with you. Free.
                        </p>
                    </div>
                    <Link
                        to="/auth"
                        data-testid="wins-cta-strip-btn"
                        className="h-12 px-5 inline-flex items-center gap-2 rounded-[12px] text-white font-semibold shrink-0"
                        style={{ background: "var(--speakup-red)" }}
                    >
                        Raise your complaint <ArrowRight size={14} />
                    </Link>
                </div>
            </main>

            <footer className="max-w-5xl mx-auto px-5 py-10 text-xs text-[var(--speakup-text-muted)]">
                Wins on this page are user-submitted, opt-in, and anonymised. SpeakUp never publishes phone numbers,
                names, or account/card digits. Want yours removed? Sign in and head to <em>Account → Delete my data</em>.
            </footer>
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div className="speakup-card p-4">
            <div className="text-[11px] uppercase tracking-wider text-[var(--speakup-text-muted)]">{label}</div>
            <div className="display text-[26px] mt-1 tabular">{value}</div>
        </div>
    );
}

function WinCard({ win }) {
    const issue = ISSUE_LABELS[win.issue_category] || win.issue_category || "Issue resolved";
    return (
        <div className="speakup-card p-5" data-testid={`win-card-${win.id}`}>
            <div className="flex items-start gap-3">
                <div
                    className="h-10 w-10 rounded-full flex items-center justify-center text-white shrink-0"
                    style={{ background: "var(--speakup-resolved)" }}
                >
                    <Trophy size={18} />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-[12px] uppercase tracking-wider text-[var(--speakup-text-muted)]">
                        {win.entity_name} · {win.entity_type}
                    </div>
                    <div className="display text-[18px] mt-1 leading-snug">
                        {win.amount_shown && win.amount_disputed != null
                            ? `${fmtMoney(win.amount_disputed)} resolved`
                            : "Resolved"}
                        {win.days_to_resolve != null ? (
                            <span className="text-[var(--speakup-text-muted)] font-medium">
                                {" "}
                                · {win.days_to_resolve}{" "}
                                {win.days_to_resolve === 1 ? "day" : "days"}
                            </span>
                        ) : null}
                    </div>
                    <div className="text-[13px] text-[var(--speakup-text-muted)] mt-1">{issue}</div>
                    {win.note && <div className="text-[14px] mt-2 text-[var(--speakup-text-strong)]">"{win.note}"</div>}
                    <div className="text-[11px] text-[var(--speakup-text-muted)] mt-3 tabular">{fmtDate(win.created_at)}</div>
                </div>
            </div>
        </div>
    );
}
